import React, { useState } from 'react';
import { DotsThreeIcon } from '@phosphor-icons/react';
import {
  Box,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  useTheme,
} from '@mui/material';
import { DoneAllSharp, PersonAdd } from '@mui/icons-material';
import InitialsAvatar from './InitialsAvatar';
import { font } from '../../utils/fontHelper';
import { glassStyles } from '../../tokens/designTokens';

interface UserCardVerticalProps {
  id: string;
  name: string;
  avatar: string | undefined;
  isFollowing?: boolean;
  isCurrentUser?: boolean;
  showFollowButton?: boolean;
  onUserClick?: (userId: string) => void;
  onFollowToggle?: (userId: string) => Promise<void>;
  onDismiss?: (userId: string) => void;
}

const UserCardVertical: React.FC<UserCardVerticalProps> = ({
  id,
  name,
  avatar,
  isFollowing = false,
  isCurrentUser = false,
  showFollowButton = true,
  onUserClick,
  onFollowToggle,
  onDismiss,
}) => {
  const isDark = useTheme().palette.mode === 'dark';
  const [isLoading, setIsLoading] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const handleFollowClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onFollowToggle || isCurrentUser || isLoading) return;

    setIsLoading(true);
    try {
      await onFollowToggle(id);
    } catch (error) {
      console.error('Error toggling follow status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUserClick = () => {
    if (onUserClick) {
      onUserClick(id);
    }
  };

  const getCardBackground = () => {
    return isDark ? '#2C2C2E' : '#F0EFF0';
  };

  const getPrimaryTextColor = () => {
    return isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.9)';
  };

  /** Match UserListingCard follow badge */
  const getFollowBadgeBg = () => {
    if (isFollowing) {
      return isDark ? 'rgba(48, 209, 88, 0.7)' : 'rgba(52, 199, 89, 0.7)';
    }
    return isDark ? 'rgba(175, 174, 243, 0.2)' : 'rgba(88, 86, 214, 0.2)';
  };

  const getFollowBadgeIconColor = () => {
    return isDark ? 'rgba(255, 255, 255, 0.87)' : 'rgba(255, 255, 255, 0.7)';
  };

  return (
    <Box
      onClick={handleUserClick}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        px: 1.5,
        pt: 1.25,
        pb: onDismiss ? 2.75 : 2,
        bgcolor: getCardBackground(),
        borderRadius: '12px',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        position: 'relative',
        height: '100%',
        overflow: 'hidden',
        '&:hover': {
          bgcolor: isDark ? '#3A3A3C' : '#E8E8ED',
          transform: 'translateY(-4px)',
        },
      }}
    >
      {onDismiss && (
        <>
          <IconButton
            size="small"
            aria-label="Suggestion options"
            aria-haspopup="true"
            aria-expanded={Boolean(menuAnchor)}
            onClick={(e) => {
              e.stopPropagation();
              setMenuAnchor(e.currentTarget);
            }}
            sx={{
              position: 'absolute',
              right: 2,
              bottom: 2,
              zIndex: 2,
              color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
              '&:hover': {
                color: isDark ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.75)',
                bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
              },
            }}
          >
            <DotsThreeIcon size={14}  weight="bold" />
          </IconButton>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
            onClick={(e) => e.stopPropagation()}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            PaperProps={{
              sx: {
                ...glassStyles.menu(isDark),
                fontFamily: font(),
                minWidth: 180,
              },
            }}
          >
            <MenuItem
              onClick={(e) => {
                e.stopPropagation();
                setMenuAnchor(null);
                onDismiss(id);
              }}
            >
              Hide suggestion
            </MenuItem>
          </Menu>
        </>
      )}

      {!isCurrentUser && showFollowButton && (
        <Box
          onClick={handleFollowClick}
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            zIndex: 2,
            width: 29,
            height: 34,
            bgcolor: getFollowBadgeBg(),
            borderRadius: '0px 8px 0px 12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'opacity 0.2s ease',
            '&:hover': { opacity: 0.85 },
          }}
        >
          {isLoading ? (
            <CircularProgress size={16} sx={{ color: getFollowBadgeIconColor() }} />
          ) : isFollowing ? (
            <DoneAllSharp sx={{ fontSize: 16, color: getFollowBadgeIconColor() }} />
          ) : (
            <PersonAdd sx={{ fontSize: 16, color: getFollowBadgeIconColor() }} />
          )}
        </Box>
      )}

      <Box sx={{ position: 'relative', mb: 1.5, mt: 0.5 }}>
        <InitialsAvatar
          src={avatar}
          name={name}
          size={72}
          sx={{
            border: `2px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
          }}
        />
      </Box>

      <Typography
        variant="body1"
        sx={{
          fontFamily: font(),
          fontWeight: 500,
          fontSize: 15,
          lineHeight: 1.35,
          color: getPrimaryTextColor(),
          textAlign: 'center',
          alignSelf: 'stretch',
          minWidth: 0,
          width: '100%',
          px: 0.5,
          overflow: 'hidden',
          overflowWrap: 'break-word',
          wordBreak: 'break-word',
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: 2,
          textOverflow: 'ellipsis',
        }}
      >
        {name}
      </Typography>
    </Box>
  );
};

export default UserCardVertical;
